// src/pages/userprofile/UserprofileDetailPage.tsx
// Public profile page — social-media-style layout with banner, avatar, contacts, about.

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useUserprofile, useUserprofileAvatar, useUserprofileBanner, useNotificationsContact, useI18n, useAuth } from '@react/hooks';
import {
  Button, Card, CardHeader, CardTitle, CardContent, Alert, Badge, ErrorAlert,
} from '@react/components';
import { StatusBadge } from '../../components/StatusBadge';
import { cn } from '@core/ui';
import {
  formatProfileStatus, genderOptions,
  profileVisibilityOptions, languageOptions, timezoneOptions,
} from '../../features/userprofile/logic';
import type { Userprofile, Avatar, Banner } from '@core/types/userprofile';
import type { Contact } from '@core/types/notifications';
import { useOpenDirectConversation } from '@/features/chat/useOpenDirectConversation';
import { EntityAvatar, avatarImage } from '@/components/EntityAvatar';
import { MediaImage } from '@/components/MediaImage';

// ─── SVG Icons (inline, no dependency) ─────────────────────────────

const IconMapPin = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
  </svg>
);

const IconCalendar = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
  </svg>
);

const IconGlobe = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
  </svg>
);

const IconClock = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconUser = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

const IconCake = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8.25v-1.5m0 1.5c-1.355 0-2.697.056-4.024.166C6.845 8.51 6 9.473 6 10.608v2.513m6-4.871c1.355 0 2.697.056 4.024.166C17.155 8.51 18 9.473 18 10.608v2.513M15 8.25v-1.5m-6 1.5v-1.5m12 9.75l-1.5.75a3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0L3 16.5m15-3.379a48.474 48.474 0 00-6-.371c-2.032 0-4.034.126-6 .371m12 0c.39.049.777.102 1.163.16 1.07.16 1.837 1.094 1.837 2.175v5.169c0 .621-.504 1.125-1.125 1.125H4.125A1.125 1.125 0 013 20.625v-5.17c0-1.08.768-2.014 1.837-2.174A47.78 47.78 0 016 13.12M12 8.25a2.25 2.25 0 01-2.25-2.25 2.25 2.25 0 112.252-2.25H12v4.5z" />
  </svg>
);

const IconShield = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
  </svg>
);

const IconPencil = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
  </svg>
);

const IconEye = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.64 0 8.577 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.64 0-8.577-3.007-9.963-7.178z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const IconBan = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
  </svg>
);

const IconEnvelope = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
  </svg>
);

const IconPhone = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
  </svg>
);

const IconChat = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
  </svg>
);

const IconLink = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
  </svg>
);

const IconCheckBadge = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
  </svg>
);

// ─── Contact type helpers ─────────────────────────────────────────────

const contactTypeConfig: Record<string, {
  icon: React.FC<{ className?: string }>;
  labelKey: string;
  href?: (value: string) => string;
}> = {
  CONTACT_TYPE_EMAIL: { icon: IconEnvelope, labelKey: 'contact.email', href: (v) => `mailto:${v}` },
  CONTACT_TYPE_PHONE: { icon: IconPhone, labelKey: 'contact.phone', href: (v) => `tel:${v}` },
  CONTACT_TYPE_TELEGRAM: { icon: IconChat, labelKey: 'contact.telegram', href: (v) => `https://t.me/${v.replace('@', '')}` },
  CONTACT_TYPE_WHATSAPP: { icon: IconChat, labelKey: 'contact.whatsapp' },
  CONTACT_TYPE_VK: { icon: IconLink, labelKey: 'contact.vk', href: (v) => v.startsWith('http') ? v : `https://vk.com/${v}` },
  CONTACT_TYPE_VIBER: { icon: IconChat, labelKey: 'contact.viber' },
  CONTACT_TYPE_SLACK: { icon: IconChat, labelKey: 'contact.slack' },
  CONTACT_TYPE_DISCORD: { icon: IconChat, labelKey: 'contact.discord' },
  CONTACT_TYPE_ADDRESS: { icon: IconMapPin, labelKey: 'contact.address' },
  CONTACT_TYPE_SOCIAL: { icon: IconLink, labelKey: 'contact.social', href: (v) => v.startsWith('http') ? v : `https://${v}` },
};

// ─── Helpers ─────────────────────────────────────────────────────────

function formatDate(ts: unknown): string {
  if (!ts) return '';
  try {
    const d = new Date(ts as string);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return String(ts);
  }
}

function formatDateShort(ts: unknown): string {
  if (!ts) return '';
  try {
    const d = new Date(ts as string);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
  } catch {
    return String(ts);
  }
}

function findLabel(options: readonly { value: string; labelKey?: string; label?: string }[], value: string, t: (k: string) => string): string {
  const opt = options.find((o) => o.value === value);
  if (!opt) return value;
  return opt.labelKey ? t(opt.labelKey) : (opt.label ?? value);
}

// ─── Info Row ───────────────────────────────────────────────────────

function InfoRow({ icon: Icon, label, value, className }: {
  icon: React.FC<{ className?: string }>;
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  if (!value) return null;
  return (
    <div className={cn('flex items-center gap-2.5 py-2', className)}>
      <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground leading-none mb-0.5">{label}</div>
        <div className="text-sm text-foreground leading-snug">{value}</div>
      </div>
    </div>
  );
}

// ─── Privacy indicator ──────────────────────────────────────────────

function PrivacyIndicator({ enabled, label }: { enabled: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <div className={cn(
        'w-2 h-2 rounded-full shrink-0',
        enabled ? 'bg-emerald-500' : 'bg-muted-foreground/30',
      )} />
      <span className={cn('text-sm', enabled ? 'text-foreground' : 'text-muted-foreground')}>{label}</span>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────

export const UserprofileDetailPage = observer(() => {
  const profileStore = useUserprofile();
  const avatarStore = useUserprofileAvatar();
  const bannerStore = useUserprofileBanner();
  const contactStore = useNotificationsContact();
  const auth = useAuth();
  const dm = useOpenDirectConversation();
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isOwner = !!(id && auth.activeProfileId === id);
  const [avatars, setAvatars] = useState<Avatar[]>([]);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  useEffect(() => { if (id) profileStore.GetUserprofile(id); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Galleries are owner-only; visitors receive the active images on the profile.
  useEffect(() => {
    if (!id) return;
    setAvatars([]);
    setBanners([]);
    if (isOwner) {
      avatarStore.ListAvatars(id, { pagination: { limit: 50, cursor: '', direction: '' } } as never)
        .then(() => setAvatars(avatarStore.items as Avatar[]))
        .catch(() => {});
      bannerStore.ListBanners(id, { pagination: { limit: 50, cursor: '', direction: '' } } as never)
        .then(() => setBanners(bannerStore.items as Banner[]))
        .catch(() => {});
    }
    contactStore.ListContactsByEntity('user_profile', id)
      .then(() => setContacts(contactStore.items as Contact[]))
      .catch(() => {});
  }, [id, isOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  const profile = profileStore.currentItem as Userprofile | null;
  if (profileStore.isLoadingItem) {
    return (
      <div className="py-16 text-center">
        <div className="inline-block w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="mt-3 text-sm text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }
  if (!profile) return <Alert variant="error">{t('common.notFound')}</Alert>;

  const status = formatProfileStatus(profile.status ?? '');
  const fullName = [profile.first_name, profile.middle_name, profile.last_name].filter(Boolean).join(' ');
  const displayName = fullName || profile.display_name || t('common.untitled');
  const activeAvatar = avatars.find((a) => a.is_active) ?? avatars[0];
  const avatarUrl = activeAvatar?.thumbnail_url || activeAvatar?.url || avatarImage(profile.avatar);
  const activeBanner = banners.find((b) => b.is_active) ?? banners[0];
  const bannerUrl = activeBanner?.url || avatarImage(profile.banner);
  const privacy = profile.privacy_settings;

  const handleDelete = async () => {
    if (!confirm(t('userprofile.deleteConfirm'))) return;
    await profileStore.DeleteUserprofile(profile.entity?.id ?? '');
    navigate('/userprofiles');
  };

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      {/* Back link */}
      <Link to="/userprofiles" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
        </svg>
        {t('common.backToList')}
      </Link>

      {profileStore.error ? <ErrorAlert error={profileStore.error} dismissible onDismiss={() => profileStore.clearError()} /> : null}

      {/* ═══ Hero: Banner + Avatar + Name ═══ */}
      <Card className="overflow-hidden rounded-xl">
        {/* Banner */}
        {bannerUrl ? (
          <div className="h-32 sm:h-40 overflow-hidden bg-muted">
            <MediaImage source={bannerUrl} className="h-full w-full object-cover" fallbackClassName="h-full w-full" />
          </div>
        ) : (
          <div className="h-24 bg-muted sm:h-32" />
        )}

        {/* Profile info section */}
        <div className="px-5 pb-6 sm:px-8">
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            {/* Avatar */}
            <div className="-mt-16 sm:-mt-20 self-center sm:self-auto shrink-0">
              <EntityAvatar name={displayName} image={avatarUrl} resource={profile.avatar} size="xl" className="border-4 border-card" />
            </div>

            {/* Name + meta */}
            <div className="flex-1 min-w-0 pt-2 sm:pt-4 text-center sm:text-left">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground truncate">{displayName}</h1>
                <StatusBadge variant={status.variant} label={t(status.labelKey)} />
                {profile.is_default && (
                  <Badge variant="primary" className="text-xs">{t('userprofile.defaultProfile')}</Badge>
                )}
              </div>

              {profile.display_name && profile.display_name !== displayName && (
                <p className="mt-1 text-sm text-muted-foreground">{profile.display_name}</p>
              )}

              {profile.bio && (
                <p className="text-sm text-muted-foreground mt-2 max-w-lg leading-relaxed">{profile.bio}</p>
              )}

              {/* Quick info chips */}
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-1.5 mt-3">
                {profile.location && (isOwner || privacy?.show_location) && (
                  <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                    <IconMapPin className="w-3.5 h-3.5" />
                    {profile.location}
                  </span>
                )}
                {profile.language && (
                  <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                    <IconGlobe className="w-3.5 h-3.5" />
                    {findLabel(languageOptions, profile.language, t)}
                  </span>
                )}
                {profile.timezone && (
                  <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                    <IconClock className="w-3.5 h-3.5" />
                    {findLabel(timezoneOptions, profile.timezone, t)}
                  </span>
                )}
                {profile.created_at && (
                  <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                    <IconCalendar className="w-3.5 h-3.5" />
                    {t('userprofile.memberSince')} {formatDateShort(profile.created_at)}
                  </span>
                )}
              </div>
            </div>

            {!isOwner && id && (
              <div className="flex items-start self-center sm:self-start shrink-0 pt-0 sm:pt-4">
                <Button size="sm" variant="secondary" disabled={dm.isOpening} onClick={() => dm.open(id)}>
                  {t('chat.people.message')}
                </Button>
                {Boolean(dm.error) && <span className="ml-2 text-xs text-destructive">{t('chat.people.openFailed')}</span>}
              </div>
            )}

            {/* Owner controls */}
            {isOwner && (
              <div className="flex items-start gap-2 self-center sm:self-start shrink-0 pt-0 sm:pt-4">
                <Link to={`/userprofiles/${profile.entity?.id}/edit`}>
                  <Button variant="secondary" size="sm" className="gap-1.5">
                    <IconPencil className="w-4 h-4" />
                    {t('common.edit')}
                  </Button>
                </Link>
                <Button variant="danger" size="sm" onClick={handleDelete}>
                  {t('common.delete')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* ═══ Content Grid ═══ */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_18rem]">
        {/* ─── Left column: About + Privacy ─── */}
        <div className="space-y-5">
          {/* About card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <IconUser className="w-4 h-4 text-muted-foreground" />
                {t('userprofile.aboutSection')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-0 divide-y divide-border">
              <InfoRow icon={IconUser} label={t('userprofile.fullName')} value={
                [profile.first_name, profile.middle_name, profile.last_name].filter(Boolean).join(' ')
              } />
              {profile.gender && (isOwner || privacy?.show_gender) && (
                <InfoRow icon={IconUser} label={t('userprofile.gender')} value={
                  findLabel(genderOptions, profile.gender, t)
                } />
              )}
              {profile.date_of_birth && (isOwner || privacy?.show_date_of_birth) && (
                <InfoRow icon={IconCake} label={t('userprofile.dateOfBirth')} value={formatDate(profile.date_of_birth)} />
              )}
              {(isOwner || privacy?.show_location) && (
                <InfoRow icon={IconMapPin} label={t('userprofile.location')} value={profile.location} />
              )}
              <InfoRow icon={IconGlobe} label={t('userprofile.language')} value={
                profile.language ? findLabel(languageOptions, profile.language, t) : undefined
              } />
              <InfoRow icon={IconClock} label={t('userprofile.timezone')} value={
                profile.timezone ? findLabel(timezoneOptions, profile.timezone, t) : undefined
              } />
            </CardContent>
          </Card>

          {/* Contacts card */}
          {contacts.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <IconPhone className="w-4 h-4 text-muted-foreground" />
                  {t('userprofile.contacts')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-0 divide-y divide-border">
                {contacts.filter((contact) => {
                  if (isOwner) return true;
                  if (contact.type === 'CONTACT_TYPE_EMAIL' && !privacy?.show_email) return false;
                  if (contact.type === 'CONTACT_TYPE_PHONE' && !privacy?.show_phone) return false;
                  return true;
                }).map((contact) => {
                  const cfg = contactTypeConfig[contact.type ?? ''] ?? { icon: IconLink, labelKey: 'contact.other' };
                  const Icon = cfg.icon;
                  const href = cfg.href?.(contact.value);

                  return (
                    <div key={contact.entity?.id} className="flex items-center gap-2.5 py-2">
                      <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs text-muted-foreground leading-none mb-0.5">{t(cfg.labelKey)}</div>
                        <div className="text-sm text-foreground leading-snug flex items-center gap-1.5">
                          {href ? (
                            <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">
                              {contact.value}
                            </a>
                          ) : (
                            <span className="truncate">{contact.value}</span>
                          )}
                          {contact.is_verified && (
                            <IconCheckBadge className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                          )}
                          {contact.is_primary && (
                            <Badge variant="secondary" className="text-[10px] px-1 py-0 leading-tight shrink-0">{t('contact.primary')}</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Privacy card — owner only */}
          {isOwner && privacy && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <IconShield className="w-4 h-4 text-muted-foreground" />
                  {t('userprofile.privacySettings')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {privacy.visibility && (
                  <div className="mb-3 pb-3 border-b border-border">
                    <div className="flex items-center gap-2">
                      <IconEye className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">
                        {findLabel(profileVisibilityOptions, privacy.visibility, t)}
                      </span>
                    </div>
                  </div>
                )}
                <div className="space-y-0.5">
                  <PrivacyIndicator enabled={privacy.show_in_search} label={t('userprofile.showInSearch')} />
                  <PrivacyIndicator enabled={privacy.show_in_directory} label={t('userprofile.showInDirectory')} />
                  <PrivacyIndicator enabled={privacy.show_email} label={t('userprofile.showEmail')} />
                  <PrivacyIndicator enabled={privacy.show_phone} label={t('userprofile.showPhone')} />
                  <PrivacyIndicator enabled={privacy.show_location} label={t('userprofile.showLocation')} />
                  <PrivacyIndicator enabled={privacy.show_online_status} label={t('userprofile.showOnlineStatus')} />
                  <PrivacyIndicator enabled={privacy.show_activity_status} label={t('userprofile.showActivityStatus')} />
                  <PrivacyIndicator enabled={privacy.show_last_active} label={t('userprofile.showLastActive')} />
                  <PrivacyIndicator enabled={privacy.show_date_of_birth} label={t('userprofile.showDateOfBirth')} />
                  <PrivacyIndicator enabled={privacy.show_gender} label={t('userprofile.showGender')} />
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* ─── Right column: Details + Ban + Avatars ─── */}
        <div className="space-y-5">
          {/* Details card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('userprofile.profileDetails')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {isOwner && <DetailItem label={t('common.id')} value={profile.entity?.id} mono />}
                <DetailItem label={t('common.status')} value={
                  <StatusBadge variant={status.variant} label={t(status.labelKey)} />
                } />
                {isOwner && <DetailItem label={t('userprofile.displayName')} value={profile.display_name} />}
                <DetailItem label={t('userprofile.isDefault')} value={
                  <Badge variant={profile.is_default ? 'success' : 'secondary'}>
                    {profile.is_default ? t('common.yes') : t('common.no')}
                  </Badge>
                } />
                <DetailItem label={t('common.createdAt')} value={formatDate(profile.created_at)} />
                <DetailItem label={t('common.updatedAt')} value={formatDate(profile.updated_at)} />
                {profile.deleted_at && (
                  <DetailItem label={t('userprofile.deletedAt')} value={formatDate(profile.deleted_at)} />
                )}
              </div>
            </CardContent>
          </Card>

          {/* UI Settings — owner only */}
          {isOwner && profile.ui_settings && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t('userprofile.uiSettings')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <SettingChip label={t('userprofile.theme')} value={profile.ui_settings.theme} />
                  <SettingChip label={t('userprofile.language')} value={profile.ui_settings.language} />
                  <SettingChip label={t('userprofile.timezone')} value={profile.ui_settings.timezone} />
                  <SettingChip label={t('userprofile.dateFormat')} value={profile.ui_settings.date_format} />
                  <SettingChip label={t('userprofile.timeFormat')} value={profile.ui_settings.time_format} />
                  <SettingChip label={t('userprofile.compactMode')} value={
                    profile.ui_settings.compact_mode ? t('common.yes') : t('common.no')
                  } />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ban info */}
          {profile.ban && (
            <Card className="border-destructive/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-destructive">
                  <IconBan className="w-4 h-4" />
                  {t('userprofile.banInfo')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg bg-destructive/5 p-4 space-y-3">
                  <div>
                    <div className="text-xs text-muted-foreground mb-0.5">{t('userprofile.banReason')}</div>
                    <div className="text-sm text-foreground">{profile.ban.reason || t('common.notSet')}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-muted-foreground mb-0.5">{t('userprofile.bannedAt')}</div>
                      <div className="text-sm text-foreground">{formatDate(profile.ban.banned_at)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-0.5">{t('userprofile.banExpires')}</div>
                      <div className="text-sm text-foreground">
                        {profile.ban.expires_at ? formatDate(profile.ban.expires_at) : (
                          <span className="text-destructive font-medium">{t('userprofile.permanent')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {profile.ban.admin_comment && (
                    <div>
                      <div className="text-xs text-muted-foreground mb-0.5">{t('userprofile.adminComment')}</div>
                      <div className="text-sm text-foreground italic">{profile.ban.admin_comment}</div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Avatar gallery */}
          {avatars.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t('userprofile.avatarGallery')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
                  {avatars.map((avatar) => {
                    const imgUrl = avatar.url || avatar.thumbnail_url;
                    return (
                      <div key={avatar.entity?.id} className="relative group">
                        <div
                          className={cn(
                            'aspect-square rounded-xl overflow-hidden border-2 transition-all cursor-pointer',
                            avatar.is_active
                              ? 'border-primary ring-2 ring-primary/20'
                              : 'border-transparent hover:border-primary/50',
                          )}
                          onClick={() => imgUrl && setLightboxUrl(imgUrl)}
                        >
                          <img src={imgUrl} alt={avatar.original_filename} className="w-full h-full object-cover" />
                        </div>
                        {avatar.is_active && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full border-2 border-card flex items-center justify-center">
                            <svg className="w-3 h-3 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Resources — owner only */}
          {isOwner && profile.resources && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t('userprofile.resources')}</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs text-muted-foreground overflow-auto bg-muted/50 rounded-lg p-3">
                  {JSON.stringify(profile.resources, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Aspects — owner only */}
          {isOwner && profile.aspects?.has_aspects && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t('userprofile.aspects')}</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs text-muted-foreground overflow-auto bg-muted/50 rounded-lg p-3">
                  {JSON.stringify(profile.aspects, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={lightboxUrl}
            alt="Preview"
            className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
});

// ─── Sub-components ─────────────────────────────────────────────────

function DetailItem({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-0.5">{label}</div>
      <div className={cn('text-sm text-foreground', mono && 'font-mono text-xs')}>{value || '—'}</div>
    </div>
  );
}

function SettingChip({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="rounded-lg bg-muted/50 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium text-foreground mt-0.5">{value}</div>
    </div>
  );
}

export default UserprofileDetailPage;
